import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { ArrowLeft, MapPin, TreeDeciduous, Ruler, Package, Lock, Shield, Truck, Calendar, MessageSquare, CheckCircle, CreditCard, Phone, AlertTriangle, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ReviewList from "@/components/ReviewList";
import SellerRating from "@/components/SellerRating";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const ListingDetail = () => {
  const { id } = useParams();
  const { user, roles } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [listing, setListing] = useState<any>(null);
  const [seller, setSeller] = useState<any>(null);
  const [fetching, setFetching] = useState(true);
  const [hasPaidOrder, setHasPaidOrder] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<any>({});
  const [selectedVolume, setSelectedVolume] = useState<number>(1);
  const [avgRating, setAvgRating] = useState<number>(0);
  const [reviewCount, setReviewCount] = useState<number>(0);

  useEffect(() => {
    if (!id) return;
    const fetchListing = async () => {
      const { data } = await supabase
        .from("listings")
        .select("*")
        .eq("id", id)
        .single();
      setListing(data);
      if (data) {
        const [{ data: sellerProfile }, { data: reviewsData }] = await Promise.all([
          supabase.from("seller_profiles_public").select("*").eq("user_id", data.seller_id).single(),
          supabase.from("reviews").select("rating").eq("seller_id", data.seller_id),
        ]);
        setSeller(sellerProfile);
        const ratings = reviewsData ?? [];
        if (ratings.length > 0) {
          setAvgRating(ratings.reduce((sum: number, r: any) => sum + r.rating, 0) / ratings.length);
          setReviewCount(ratings.length);
        }
      }
      setFetching(false);
    };

    const checkOrder = async () => {
      if (!user) return;
      const { data: orders } = await supabase
        .from("orders")
        .select("id")
        .eq("listing_id", id)
        .eq("buyer_id", user.id)
        .in("status", ["paid", "completed"])
        .limit(1);
      setHasPaidOrder((orders?.length ?? 0) > 0);
    };

    fetchListing();
    checkOrder();
  }, [id, user]);

  // Dynamic SEO title — must be called before any early return to respect hook rules
  const seoTitle = listing ? `${listing.species} à ${listing.location} - ChimayBois` : "Chargement... - ChimayBois";
  const seoDesc = listing
    ? `${listing.title} — ${listing.price_per_stere}€/stère, ${listing.seasoning}, ${listing.length}. Achetez du bois de chauffage à ${listing.location}.`
    : "";
  useDocumentTitle(seoTitle, seoDesc);

  // Initialize selectedVolume when listing loads
  useEffect(() => {
    if (listing) {
      const min = listing.min_steres ?? 1;
      setSelectedVolume(Math.max(min, 1));
    }
  }, [listing?.id]);

  if (fetching) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center text-muted-foreground">Chargement...</div>
        <Footer />
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="font-display text-2xl font-bold mb-2">Annonce introuvable</p>
            <Button variant="outline" asChild>
              <Link to="/annonces"><ArrowLeft className="h-4 w-4 mr-2" /> Retour</Link>
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const minSteres = listing.min_steres ?? 1;
  const maxSteres = listing.stock ?? listing.volume;

  const serviceFee = 10;
  const totalWood = listing.price_per_stere * selectedVolume;
  const sellerName = seller?.display_name || seller?.full_name || "Vendeur";
  const sellerRating = avgRating > 0 ? avgRating : (seller?.admin_rating ?? 0);
  const imageUrl = listing.image_urls?.[0] || "/placeholder.svg";
  const isSoldOut = listing.status === "sold_out" || listing.stock <= 0;
  const isAdmin = roles.includes("admin");
  const isOwner = user?.id === listing.seller_id;
  const canEdit = isAdmin || isOwner;

  const startEditing = () => {
    setEditForm({
      title: listing.title,
      description: listing.description || "",
      price_per_stere: listing.price_per_stere,
      stock: listing.stock,
      min_steres: (listing as any).min_steres ?? 1,
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    const { error } = await supabase
      .from("listings")
      .update({
        title: editForm.title,
        description: editForm.description,
        price_per_stere: parseFloat(editForm.price_per_stere),
        stock: parseFloat(editForm.stock),
        min_steres: parseFloat(editForm.min_steres) || 1,
      } as any)
      .eq("id", listing.id);
    if (error) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Annonce mise à jour !" });
      setEditing(false);
      // Refresh
      const { data } = await supabase.from("listings").select("*").eq("id", id).single();
      setListing(data);
    }
  };

  const handleDelete = async () => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer cette annonce ?")) return;
    const { error } = await supabase.from("listings").delete().eq("id", listing.id);
    if (error) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Annonce supprimée" });
      navigate("/annonces");
    }
  };

  const handleReservation = async () => {
    if (!user) { navigate("/auth"); return; }
    if (isSoldOut) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { listing_id: id, volume: selectedVolume },
      });
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("Pas d'URL de paiement reçue");
      }
    } catch (err: any) {
      toast({ title: "Erreur", description: err.message || "Impossible de créer le paiement", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleContact = () => {
    if (!user) { navigate("/auth"); return; }
    if (!hasPaidOrder) {
      toast({
        title: "Chat verrouillé",
        description: "Le chat et les coordonnées sont débloqués après le versement de l'acompte de 10 €.",
      });
      return;
    }
    navigate("/messages");
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="container py-6 flex-1">
        <Button variant="ghost" size="sm" asChild className="mb-4 gap-2">
          <Link to="/annonces"><ArrowLeft className="h-4 w-4" /> Retour aux annonces</Link>
        </Button>

        {/* Admin/Owner controls */}
        {canEdit && !editing && (
          <div className="flex gap-2 mb-4">
            <Button variant="outline" size="sm" className="gap-2" onClick={startEditing}>
              <Pencil className="h-4 w-4" /> Modifier
            </Button>
            <Button variant="destructive" size="sm" className="gap-2" onClick={handleDelete}>
              <Trash2 className="h-4 w-4" /> Supprimer
            </Button>
          </div>
        )}

        {/* Edit form */}
        {editing && (
          <div className="bg-card rounded-lg border p-6 mb-6 space-y-4">
            <h3 className="font-display font-semibold">Modifier l'annonce</h3>
            <div>
              <label className="text-sm font-medium mb-1 block">Titre</label>
              <Input value={editForm.title} onChange={(e) => setEditForm((f: any) => ({ ...f, title: e.target.value }))} />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description</label>
              <Textarea value={editForm.description} onChange={(e) => setEditForm((f: any) => ({ ...f, description: e.target.value }))} rows={3} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Prix/stère (€)</label>
                <Input type="number" value={editForm.price_per_stere} onChange={(e) => setEditForm((f: any) => ({ ...f, price_per_stere: e.target.value }))} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Stock (stères)</label>
                <Input type="number" value={editForm.stock} onChange={(e) => setEditForm((f: any) => ({ ...f, stock: e.target.value }))} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Min. livraison</label>
                <Input type="number" value={editForm.min_steres} onChange={(e) => setEditForm((f: any) => ({ ...f, min_steres: e.target.value }))} />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={saveEdit} className="gap-2"><Pencil className="h-4 w-4" /> Enregistrer</Button>
              <Button variant="outline" onClick={() => setEditing(false)}>Annuler</Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 space-y-6">
            <div className="rounded-lg overflow-hidden aspect-[16/10] relative">
              <img src={imageUrl} alt={`Stère de bois de chauffage ${listing.species} à ${listing.location} - ChimayBois`} className="w-full h-full object-cover" loading="lazy" />
              {isSoldOut && (
                <div className="absolute inset-0 bg-foreground/60 flex items-center justify-center">
                  <Badge className="bg-destructive text-destructive-foreground text-lg px-4 py-2 gap-2">
                    <AlertTriangle className="h-5 w-5" /> Épuisé
                  </Badge>
                </div>
              )}
            </div>

            <div>
              <div className="flex items-start justify-between gap-4 mb-4">
                <h1 className="font-display text-2xl md:text-3xl font-bold">{listing.title}</h1>
                {listing.admin_verified && (
                  <Badge className="bg-primary text-primary-foreground shrink-0 gap-1">
                    <CheckCircle className="h-3 w-3" /> Vérifié
                  </Badge>
                )}
              </div>

              <div className="flex items-center gap-3 text-sm text-muted-foreground mb-6">
                <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{listing.location}</span>
              </div>

              {listing.description && (
                <p className="text-sm text-muted-foreground mb-6">{listing.description}</p>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { icon: TreeDeciduous, label: "Essence", value: listing.species },
                  { icon: Calendar, label: "Séchage", value: listing.seasoning },
                  { icon: Ruler, label: "Longueur", value: listing.length },
                  { icon: Package, label: "Fendu", value: listing.split ? "Oui" : "Non" },
                ].map((spec) => (
                  <div key={spec.label} className="bg-card rounded-lg p-3 border">
                    <spec.icon className="h-4 w-4 text-primary mb-1" />
                    <p className="text-xs text-muted-foreground">{spec.label}</p>
                    <p className="text-sm font-semibold">{spec.value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Seller info */}
            <div className="bg-card rounded-lg border p-6">
              <h3 className="font-display font-semibold mb-3">{sellerName}</h3>
              <Link to={`/vendeur/${listing.seller_id}`} className="flex items-center gap-3 mb-4 hover:opacity-80 transition-opacity">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
                  {sellerName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-medium text-sm text-primary underline-offset-2 hover:underline">{sellerName}</p>
                  <SellerRating rating={sellerRating} size="md" />
                </div>
              </Link>

              {hasPaidOrder ? (
                <Button variant="default" className="w-full gap-2" onClick={handleContact}>
                  <MessageSquare className="h-4 w-4" /> Contacter {sellerName}
                </Button>
              ) : (
                <>
                  <div className="bg-muted rounded-md p-4 flex items-center gap-3 text-sm text-muted-foreground">
                    <Lock className="h-5 w-5 shrink-0 text-primary" />
                    <span>Le chat et les coordonnées sont débloqués après le versement de l'acompte de 10 €.</span>
                  </div>
                  <Button variant="outline" className="mt-4 w-full gap-2 opacity-60 cursor-not-allowed" disabled>
                    <Lock className="h-4 w-4" /> Chat verrouillé
                  </Button>
                </>
              )}
            </div>

            <div className="bg-card rounded-lg border p-6">
              <ReviewList sellerId={listing.seller_id} />
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-2">
            <div className="sticky top-20 bg-card rounded-lg border p-6 shadow-card space-y-5">
              <div>
                <p className="text-sm text-muted-foreground">Prix</p>
                <p className="font-display text-3xl font-bold text-primary">{listing.price_per_stere}€<span className="text-base font-normal text-muted-foreground">/stère</span></p>
              </div>

              <div className="border-t pt-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Stock disponible</span>
                  <span className={`font-medium ${isSoldOut ? "text-destructive" : ""}`}>
                    {isSoldOut ? "Épuisé" : `${listing.stock} stères`}
                  </span>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-muted-foreground">Volume souhaité</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min={minSteres}
                      max={maxSteres}
                      step={1}
                      value={selectedVolume}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        if (!isNaN(v)) setSelectedVolume(v);
                      }}
                      className="w-24 text-center"
                      disabled={isSoldOut}
                    />
                    <span className="text-sm text-muted-foreground">stères (min. {minSteres} — max. {maxSteres})</span>
                  </div>
                  {selectedVolume < minSteres && (
                    <p className="text-xs text-destructive mt-1">Le minimum pour livraison est de {minSteres} stères.</p>
                  )}
                  {selectedVolume > maxSteres && (
                    <p className="text-xs text-destructive mt-1">Le stock disponible est de {maxSteres} stères.</p>
                  )}
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total bois</span>
                  <span className="font-medium">{totalWood.toFixed(2)}€</span>
                </div>
                {minSteres > 1 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Minimum livraison</span>
                    <span className="font-medium">{minSteres} stères</span>
                  </div>
                )}
              </div>

              <div className="bg-accent/15 rounded-lg p-4 border border-accent/30">
                <p className="text-xs font-medium text-accent-foreground mb-1">Acompte à payer ici</p>
                <p className="text-2xl font-display font-bold text-accent-foreground">{serviceFee}€ <span className="text-sm font-normal text-muted-foreground">(déductibles du prix final)</span></p>
                <p className="text-xs text-muted-foreground mt-1">Frais de réservation sécurisée : débloquez le contact du vendeur et recevez votre Wood-Pass.</p>
              </div>

              {isSoldOut ? (
                <Button className="w-full" size="lg" disabled>
                  <AlertTriangle className="h-4 w-4 mr-2" /> Stock épuisé
                </Button>
              ) : (
                <Button className="w-full" size="lg" onClick={handleReservation} disabled={loading || selectedVolume < minSteres || selectedVolume > maxSteres}>
                  <Lock className="h-4 w-4 mr-2" />
                  {loading ? "Redirection..." : `Réserver ${selectedVolume} stères — ${serviceFee}€`}
                </Button>
              )}

              <div className="flex items-start gap-2 text-xs text-muted-foreground">
                <Shield className="h-4 w-4 shrink-0 text-primary mt-0.5" />
                <span>Paiement sécurisé de 10 €. Le vendeur déduira ce montant lors de la livraison.</span>
              </div>

              <div className="flex items-start gap-2 text-xs text-muted-foreground">
                <Truck className="h-4 w-4 shrink-0 text-primary mt-0.5" />
                <span>Le solde à payer au vendeur à la livraison sera de {(totalWood - serviceFee).toFixed(2)}€ (total {totalWood.toFixed(2)}€ − acompte {serviceFee}€).</span>
              </div>
            </div>
          </div>
        </div>

        {/* Comment ça marche */}
        <div className="mt-12 bg-card rounded-lg border p-8">
          <h2 className="font-display text-2xl font-bold text-center mb-8">Comment ça marche ?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: CreditCard, title: "Réservez votre bois", desc: "Cliquez sur « Réserver » et réglez un acompte fixe de 10 € via Stripe. Ces 10 € servent de frais de réservation sécurisée." },
              { icon: Phone, title: "Déblocage immédiat", desc: "Une fois le paiement validé, vous débloquez l'accès au chat privé et recevez les coordonnées du vendeur pour finaliser la livraison." },
              { icon: Truck, title: "Livraison & paiement final", desc: "Le vendeur vous livre. Il déduit les 10 € déjà payés du montant total. Vous ne payez que le solde restant à la livraison." },
            ].map((step, i) => (
              <div key={step.title} className="text-center">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <step.icon className="h-5 w-5 text-primary" />
                </div>
                <div className="text-xs font-bold text-accent-foreground bg-accent/20 rounded-full inline-block px-3 py-1 mb-2">
                  Étape {i + 1}
                </div>
                <h3 className="font-display text-base font-semibold mb-1">{step.title}</h3>
                <p className="text-sm text-muted-foreground">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ListingDetail;
